/**
 * Wire and merge types for the terminal session-sync API
 * (`/sync/v1/*` on the user's gateway instance — see
 * api/paradex/terminal/SESSION-PERSISTENCE.md).
 *
 * The document payloads are structurally generic: both the Paradex web app
 * and the Paradigm desktop app supply their own `ChatMessage` /
 * `ChatSession` / `CustomFolder` shapes, which satisfy the `*Like`
 * constraints below. The sync server itself never inspects them.
 */

/** Minimal shape a chat message must have for merging. */
export interface SyncMessageLike {
  readonly id: string;
  readonly timestamp: number;
}

/** Minimal shape a session index entry must have for merging. */
export interface SyncSessionLike {
  readonly sessionId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
}

/** Minimal shape a custom folder must have for merging. */
export interface SyncFolderLike {
  readonly id: string;
  readonly createdAt: number;
}

/**
 * Deletion marker. Tombstones ride inside the index document so that a
 * deletion on device A beats a stale index upload from device B — without
 * them, a merge would resurrect deleted sessions.
 */
export interface Tombstone {
  readonly kind: 'session' | 'folder';
  readonly id: string;
  readonly deletedAt: number;
}

/** The index document: sessions list + custom folders + tombstones. */
export interface SyncIndexDoc<
  S extends SyncSessionLike,
  F extends SyncFolderLike,
> {
  readonly sessions: readonly S[];
  readonly folders: readonly F[];
  readonly deleted: readonly Tombstone[];
}

/** Per-session transcript document. */
export interface SyncMessagesDoc<M extends SyncMessageLike> {
  readonly messages: readonly M[];
}

/** Server envelope: rev is the optimistic-concurrency token (ETag). */
export interface SyncEnvelope<T> {
  readonly rev: number;
  readonly updatedAt?: string;
  readonly doc: T;
}

/** GET /sync/v1/snapshot response. */
export interface SyncSnapshot<
  M extends SyncMessageLike,
  S extends SyncSessionLike,
  F extends SyncFolderLike,
> {
  readonly index: SyncEnvelope<SyncIndexDoc<S, F>> | null;
  readonly uiState: SyncEnvelope<Record<string, unknown>> | null;
  readonly sessions: Readonly<Record<string, SyncEnvelope<SyncMessagesDoc<M>>>>;
  readonly sessionList: readonly { id: string; rev: number; bytes: number }[];
}

/** Identifies one syncable document. */
export type SyncDocRef =
  | { readonly kind: 'index' }
  | { readonly kind: 'ui-state' }
  | { readonly kind: 'session'; readonly sessionId: string };

export function syncDocKey(ref: SyncDocRef): string {
  return ref.kind === 'session' ? `session:${ref.sessionId}` : ref.kind;
}
